import React, { useEffect, useState } from "react";

const SCRIPT_ID = "bhashini-translation-script";
const SCRIPT_SRC =
  "https://translation-plugin.bhashini.co.in/v3/website_translation_utility.js";

const ZOOM_KEY = "uiTextZoom";
const ZOOM_MIN = 80;
const ZOOM_MAX = 140;
const ZOOM_STEP = 10;

const readZoom = () => {
  try {
    const saved = parseInt(localStorage.getItem(ZOOM_KEY), 10);
    return Number.isFinite(saved) ? Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, saved)) : 100;
  } catch (e) {
    return 100;
  }
};

const applyZoom = (value) => {
  document.body.style.zoom = value === 100 ? "" : `${value}%`;
};

const zoomBtnStyle = {
  border: "1px solid #04151e",
  background: "#fff",
  color: "#04151e",
  borderRadius: "4px",
  padding: "0 8px",
  fontWeight: 700,
  fontSize: "13px",
  lineHeight: "22px",
  cursor: "pointer",
};

/**
 * Shared header control: A- / A+ text zoom and the Bhashini language widget.
 * The chosen language is stored by the Bhashini plugin in
 * localStorage.preferredLanguage, so loading the script on every page that
 * renders this control keeps the language the same across pages.
 *
 * showLanguage={false} renders only A- / A+ (used where the plugin container
 * is already present elsewhere on the page).
 */
export default function BhashiniTranslator({ showLanguage = true, className = "" }) {
  const [zoom, setZoom] = useState(readZoom);

  useEffect(() => {
    applyZoom(zoom);
    try {
      localStorage.setItem(ZOOM_KEY, String(zoom));
    } catch (e) {
      /* storage unavailable */
    }
  }, [zoom]);

  useEffect(() => {
    if (!showLanguage) return undefined;

    // Re-inject on mount: the plugin looks for .bhashini-plugin-container
    // only when the script runs, and this container is new on every page.
    const old = document.getElementById(SCRIPT_ID);
    if (old) old.remove();

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    document.body.appendChild(script);

    return () => {
      const existing = document.getElementById(SCRIPT_ID);
      if (existing) existing.remove();
    };
  }, [showLanguage]);

  const change = (delta) =>
    setZoom((z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z + delta)));

  return (
    <div
      className={className}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        color: "#04151e",
        zIndex: 99999,
        position: "relative",
      }}
    >
      <button
        type="button"
        title="Decrease text size"
        aria-label="Decrease text size"
        style={zoomBtnStyle}
        disabled={zoom <= ZOOM_MIN}
        onClick={() => change(-ZOOM_STEP)}
      >
        A-
      </button>
      <button
        type="button"
        title="Increase text size"
        aria-label="Increase text size"
        style={zoomBtnStyle}
        disabled={zoom >= ZOOM_MAX}
        onClick={() => change(ZOOM_STEP)}
      >
        A+
      </button>
      {showLanguage && (
        <>
          {/* The plugin picks left/right from measurements that break under
              body zoom, so the list could open off-screen. Pin it directly
              below the button, right-aligned to it. */}
          <style>{`
            .bhashini-plugin-container .bhashini-dropdown-content {
              top: 100% !important;
              bottom: auto !important;
              left: auto !important;
              right: 0 !important;
              margin-top: 6px;
            }
          `}</style>
          <span>Bhashini :</span>
          <div
            className="bhashini-plugin-container"
            style={{
              background: "#ecf2f5",
              borderRadius: "6px",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              position: "relative",
              zIndex: 99999,
            }}
          />
        </>
      )}
    </div>
  );
}
